import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { onboardingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  User, Briefcase, FileText, Bell, Megaphone,
  CheckCircle, Circle, X, Sparkles, ArrowRight
} from 'lucide-react';

const iconMap = {
  user: User,
  briefcase: Briefcase,
  file_plus: FileText,
  bell: Bell,
  megaphone: Megaphone,
};

export function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadChecklist(); }, []);

  const loadChecklist = async () => {
    try {
      const res = await onboardingAPI.checklist();
      setData(res.data);
    } catch {
      setData({ dismissed: true });
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await onboardingAPI.dismiss();
      setData({ dismissed: true });
    } catch {}
  };

  if (loading || !data || data.dismissed || data.total === 0) return null;

  const progress = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;
  const allDone = data.completed === data.total;

  return (
    <Card className="mb-8 animate-slideIn border-l-4 border-l-sky-500" data-testid="onboarding-checklist">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Sparkles size={16} className="text-sky-500" />
          {allDone ? 'Onboarding Complete!' : 'Getting Started'}
        </CardTitle>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{data.completed}/{data.total} steps</span>
          <Button variant="ghost" size="sm" onClick={handleDismiss} className="h-6 w-6 p-0" data-testid="dismiss-checklist-btn">
            <X size={14} className="text-slate-400" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Progress value={progress} className="h-1.5 mb-4" />

        <div className="space-y-2">
          {data.checklist.map((step) => {
            const Icon = iconMap[step.icon] || Circle;
            return (
              <Link
                key={step.id}
                to={step.link}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all group ${
                  step.completed
                    ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/20'
                    : 'border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 dark:border-slate-700'
                }`}
                data-testid={`checklist-step-${step.id}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  step.completed
                    ? 'bg-emerald-100 dark:bg-emerald-800'
                    : 'bg-slate-100 dark:bg-slate-700 group-hover:bg-sky-100'
                }`}>
                  {step.completed ? (
                    <CheckCircle size={16} className="text-emerald-600" />
                  ) : (
                    <Icon size={16} className="text-slate-400 group-hover:text-sky-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    step.completed ? 'text-emerald-700 dark:text-emerald-400 line-through' : 'text-slate-900 dark:text-slate-100'
                  }`}>
                    {step.title}
                  </p>
                  <p className="text-[10px] text-slate-500">{step.description}</p>
                </div>
                {!step.completed && (
                  <ArrowRight size={14} className="text-slate-300 group-hover:text-sky-400 shrink-0" />
                )}
              </Link>
            );
          })}
        </div>

        {allDone && (
          <div className="mt-4 text-center">
            <p className="text-sm text-emerald-600 font-medium">You're all set! Welcome aboard.</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={handleDismiss}>
              Dismiss Checklist
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
